const STALE_THRESHOLD_MS = 6 * 60 * 60 * 1000;

let initialized = false;
let checking = false;
let reloadedBuildId: string | null = null;
let listenerRef: (() => void) | null = null;

async function defaultFetchVersion(): Promise<{ buildId: string }> {
  const response = await fetch(`${import.meta.env.BASE_URL}version.json`, {
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`version.json fetch failed: ${String(response.status)}`);
  }
  return response.json() as Promise<{ buildId: string }>;
}

interface StaleAppReloadDeps {
  threshold?: number;
  reload?: () => void;
  fetchVersion?: () => Promise<{ buildId: string }>;
  currentBuildId?: string;
  isProd?: boolean;
}

export function initStaleAppReload(deps: StaleAppReloadDeps = {}): void {
  const {
    threshold = STALE_THRESHOLD_MS,
    reload = () => { window.location.reload(); },
    fetchVersion = defaultFetchVersion,
    currentBuildId = __BUILD_ID__,
    isProd = import.meta.env.PROD,
  } = deps;

  if (!isProd) {
    return;
  }
  if (initialized) {
    return;
  }
  initialized = true;

  const pageLoadTimestamp = Date.now();

  function handleVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }
    if (Date.now() - pageLoadTimestamp < threshold) {
      return;
    }
    if (checking) {
      return;
    }
    checking = true;

    void fetchVersion()
      .then(({ buildId }) => {
        if (buildId === currentBuildId) {
          return;
        }
        if (buildId === reloadedBuildId) {
          return;
        }
        reloadedBuildId = buildId;
        reload();
      })
      .catch(() => undefined)
      .finally(() => {
        checking = false;
      });
  }

  listenerRef = handleVisibilityChange;
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

export function resetStaleAppReloadForTesting(): void {
  if (listenerRef) {
    document.removeEventListener('visibilitychange', listenerRef);
    listenerRef = null;
  }
  initialized = false;
  checking = false;
  reloadedBuildId = null;
}
